import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { PercentageInput } from '@client/components/_ui/data-entry/PercentageInput';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { CostCodeTag } from '@client/components/claims/financials/CostCodeTag';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { trpc } from '@client/trpc/client';
import { buildCurrencyInputRules, cn } from '@client/utils';
import { type ConfigurableCompensationPaymentField, isCompensationPaymentFieldVisible } from '@client/utils/financials';
import { buildPercentageInputRules } from '@client/utils/validation/percentage';
import {
    COMPENSATION_PAYMENT_USE_TYPES_LABELS,
    type CompensationPaymentUseType,
} from '@shared/constants/business/compensation-payment';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Checkbox, Col, Form, Input, Row, Select, Tooltip } from 'antd';
import { Decimal } from 'decimal.js';
import { CircleQuestionMark } from 'lucide-react';
import { useEffect, useId } from 'react';
import { claimFormStore } from '../../../store';
import { useCompensationStore } from './store';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/compensations/add/compensation-payments/add',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Add payment item',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CompensationPaymentAdd />
        </PageController>
    );
}

type CompensationPayment = {
    reserveId: bigint;
    description: string;

    isGSTApplicable?: boolean;
    doYouHoldValidTaxInvoice?: boolean;
    isSupplyUnderMinimum?: boolean;
    isThisPurchaseOfMotorVehicle?: boolean;
    isThisPurchaseOfRealPropertyUnderMarginScheme?: boolean;
    isPreApproved?: boolean;

    useType?: CompensationPaymentUseType;

    inputTaxCreditEntitlement?: Decimal;

    netAmount?: Decimal;
    gstAmount?: Decimal;
    totalAmount?: Decimal;
};

function useIsFieldVisible() {
    const compensation = useCompensationStore((state) => state.compensation);
    const { data: transactionType } = trpc.transactionType.getTransactionType.useQuery(
        compensation?.transactionTypeId ? { id: compensation.transactionTypeId } : skipToken,
    );

    return (name: ConfigurableCompensationPaymentField) =>
        isCompensationPaymentFieldVisible(name, {
            transactionNature: transactionType?.transactionNature,
            isRegisteredForGst: compensation?.isRegisteredForGst,
            payeeType: compensation?.payeeType,
        });
}

const FormItem = Form.Item<CompensationPayment>;

function CompensationPaymentAdd() {
    const formId = useId();
    const isReadonly = claimFormStore.use.isReadonly();
    const claim = claimFormStore.use.formData();
    const compensation = useCompensationStore((state) => state.compensation);
    const compensationId = useCompensationStore((state) => state.compensationId);
    const compensationPaymentCount = useCompensationStore((state) => state.compensationPaymentCount);
    const { trpcUtils } = useTrpc();
    const { data } = trpc.reserve.listReservesForSelect.useQuery({
        claimId: claim.id,
        compensationId,
    });
    const { data: productType } = trpc.productType.getProductType.useQuery(
        claim.productTypeId ? { id: claim.productTypeId } : skipToken,
    );
    const { mutate: createCompensationPayment, isPending } =
        trpc.compensationPayment.createCompensationPayment.useMutation();
    const { mutate: calculateGST } = trpc.compensationPayment.calculateGST.useMutation();
    const { formatCurrency } = useFormatter();
    const setCompensationPaymentCount = useCompensationStore((state) => state.setCompensationPaymentCount);
    const isFieldVisible = useIsFieldVisible();
    const navigate = useNavigate();

    const [form] = Form.useForm<CompensationPayment>();
    const isGSTApplicable = Form.useWatch(({ isGSTApplicable }) => isGSTApplicable, form);
    const doYouHoldValidTaxInvoice = Form.useWatch(({ doYouHoldValidTaxInvoice }) => doYouHoldValidTaxInvoice, form);
    const isThisPurchaseOfMotorVehicle = Form.useWatch(
        ({ isThisPurchaseOfMotorVehicle }) => isThisPurchaseOfMotorVehicle,
        form,
    );
    const isThisPurchaseOfRealPropertyUnderMarginScheme = Form.useWatch(
        ({ isThisPurchaseOfRealPropertyUnderMarginScheme }) => isThisPurchaseOfRealPropertyUnderMarginScheme,
        form,
    );
    const useType = Form.useWatch(({ useType }) => useType, form);

    const canModify = !isReadonly && compensation;

    useEffect(() => {
        if (!canModify) {
            navigate({ moveInMatches: -1 });
        }
    }, [canModify]);

    useEffect(() => {
        if (compensation?.inputTaxCreditEntitlement) {
            form.setFieldsValue({
                inputTaxCreditEntitlement: compensation?.inputTaxCreditEntitlement,
            });
        }
    }, [compensation?.inputTaxCreditEntitlement]);

    if (!canModify) {
        return null;
    }

    return (
        <OverlayPage
            afterClose={() => {
                form.resetFields();
                navigate({ moveInMatches: -1 });
            }}
            title="Add payment item"
            size="middle"
            footer={({ onClose }) => (
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" htmlType="submit" form={formId} loading={isPending}>
                        Save
                    </Button>
                </>
            )}
        >
            {({ onClose }) => (
                <Form
                    form={form}
                    id={formId}
                    layout="vertical"
                    disabled={isPending}
                    className={cn(
                        'mb-0',
                        '[&_.ant-checkbox-label]:flex',
                        '[&_.ant-checkbox-label]:items-center',
                        '[&_.ant-checkbox-label]:gap-1',
                    )}
                    onValuesChange={(changedFields, { description: _, ...values }) => {
                        if (
                            values.reserveId &&
                            (compensation.payeeType !== 'POLICY_HOLDER' || values.useType) &&
                            (changedFields.netAmount ||
                                changedFields.totalAmount ||
                                values.netAmount ||
                                values.totalAmount)
                        ) {
                            calculateGST(
                                {
                                    ...values,
                                    ...(changedFields.netAmount || changedFields.totalAmount
                                        ? {
                                              netAmount: changedFields.netAmount || undefined,
                                              totalAmount: changedFields.totalAmount || undefined,
                                          }
                                        : {
                                              netAmount: values.netAmount || undefined,
                                              totalAmount: values.totalAmount || undefined,
                                          }),
                                    compensationId: compensationId!,
                                },
                                {
                                    onSuccess(data) {
                                        form.setFieldsValue({
                                            netAmount: data.netAmount,
                                            gstAmount: data.gstAmount,
                                            totalAmount: data.totalAmount,
                                        });
                                    },
                                    onError(error) {
                                        errorMessage.show(error);
                                    },
                                },
                            );
                        }
                    }}
                    onFinish={(values) => {
                        message.loading('Saving...');

                        if (!compensationId) {
                            return;
                        }

                        createCompensationPayment(
                            { ...values, compensationId },
                            {
                                onSuccess() {
                                    message.success('Saved.');
                                    setCompensationPaymentCount(compensationPaymentCount + 1);
                                    void trpcUtils.compensation.getCompensationSummary.invalidate();
                                    void trpcUtils.compensationPayment.listCompensationPayments.invalidate();
                                    onClose();
                                },
                                onError(error) {
                                    if (
                                        error.data?.errorId ===
                                        'compensationPayment.totalAmountGreaterThanRemainingReserveAmount'
                                    ) {
                                        message.destroy();

                                        form.setFields([
                                            {
                                                name: 'totalAmount',
                                                errors: ['Total amount is greater than remaining reserve amount.'],
                                            },
                                        ]);

                                        return;
                                    }

                                    errorMessage.show(error);
                                },
                            },
                        );
                    }}
                >
                    <FormItem name="reserveId" label="Reserve" rules={[{ required: true }]}>
                        <Select
                            options={data?.rows.map(({ id, costCodeFullName, remainingReserveAmount }) => ({
                                label: (
                                    <>
                                        Reserve - <CostCodeTag costCode={costCodeFullName} /> -{' '}
                                        {formatCurrency(remainingReserveAmount)}
                                    </>
                                ),
                                value: id,
                            }))}
                        />
                    </FormItem>
                    <FormItem name="description" label="Description" rules={[{ required: true }]}>
                        <Input.TextArea rows={4} />
                    </FormItem>

                    {isFieldVisible('isGSTApplicable') && (
                        <FormItem name="isGSTApplicable" valuePropName="checked" className="mb-0">
                            <Checkbox
                                onChange={(e) =>
                                    form.setFieldsValue({
                                        gstAmount: e.target.checked ? new Decimal(0) : undefined,
                                    })
                                }
                            >
                                GST applicable
                                <Tooltip title="If this checkbox is ticked, GST will be applied.">
                                    <CircleQuestionMark className="size-4 cursor-help text-subtle-foreground" />
                                </Tooltip>
                            </Checkbox>
                        </FormItem>
                    )}
                    {isFieldVisible('doYouHoldValidTaxInvoice') && (
                        <FormItem name="doYouHoldValidTaxInvoice" valuePropName="checked" className="mb-0">
                            <Checkbox
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        form.setFieldsValue({ isSupplyUnderMinimum: false });
                                    }
                                }}
                                disabled={!isGSTApplicable}
                            >
                                Do you hold a valid tax invoice?
                                <Tooltip
                                    title={
                                        <>
                                            <p>
                                                Tick this box if you have received a valid tax invoice from the supplier
                                                for this item. A valid tax invoice must show the supplier's ABN,
                                                identify the goods or services, and clearly state the GST amount
                                                charged.
                                            </p>
                                            <p>
                                                An invoice that is more than 4 years old is not considered a valid tax
                                                invoice and this box should not be checked if the invoice is more than 4
                                                years old.
                                            </p>
                                        </>
                                    }
                                >
                                    <CircleQuestionMark className="size-4 cursor-help text-subtle-foreground" />
                                </Tooltip>
                            </Checkbox>
                        </FormItem>
                    )}
                    {isFieldVisible('isSupplyUnderMinimum') && (
                        <FormItem name="isSupplyUnderMinimum" valuePropName="checked" className="mb-0">
                            <Checkbox disabled={doYouHoldValidTaxInvoice}>
                                Is the supply under $82.50?
                                <Tooltip
                                    title={
                                        <>
                                            If the gross amount of this item is less than $82.50 including GST, no tax
                                            invoice is required to claim ITC/DA.
                                        </>
                                    }
                                >
                                    <CircleQuestionMark className="size-4 cursor-help text-subtle-foreground" />
                                </Tooltip>
                            </Checkbox>
                        </FormItem>
                    )}
                    {isFieldVisible('isThisPurchaseOfMotorVehicle') && productType?.useCarLimitLogic && (
                        <FormItem name="isThisPurchaseOfMotorVehicle" valuePropName="checked" className="mb-0">
                            <Checkbox>
                                Is this purchase of a motor vehicle?
                                <Tooltip
                                    title={
                                        <>
                                            If this checkbox is ticked, the system will check the luxury car tax
                                            threshold and calculate the ITC on this item accordingly.
                                        </>
                                    }
                                >
                                    <CircleQuestionMark className="size-4 cursor-help text-subtle-foreground" />
                                </Tooltip>
                            </Checkbox>
                        </FormItem>
                    )}
                    {isFieldVisible('isThisPurchaseOfRealPropertyUnderMarginScheme') &&
                        productType?.useMarginScheme && (
                            <FormItem
                                name="isThisPurchaseOfRealPropertyUnderMarginScheme"
                                valuePropName="checked"
                                className="mb-0"
                            >
                                <Checkbox>
                                    Is this purchase of a real property under margin scheme?
                                    <Tooltip
                                        title={
                                            <>
                                                If this is a purchase for property under the margin scheme, you will be
                                                required to manually input the GST amount for this item.
                                            </>
                                        }
                                    >
                                        <CircleQuestionMark className="size-4 cursor-help text-subtle-foreground" />
                                    </Tooltip>
                                </Checkbox>
                            </FormItem>
                        )}
                    {isFieldVisible('isPreApproved') && (
                        <FormItem name="isPreApproved" valuePropName="checked">
                            <Checkbox>
                                Pre-approved
                                <Tooltip
                                    title={
                                        <>
                                            Tick this box if the insurer has already agreed to the use, replacement, or
                                            settlement value of this item before payment. Pre-approval helps confirm
                                            that the item meets claim and policy conditions.
                                        </>
                                    }
                                >
                                    <CircleQuestionMark className="size-4 cursor-help text-subtle-foreground" />
                                </Tooltip>
                            </Checkbox>
                        </FormItem>
                    )}

                    <Row gutter={16}>
                        {isFieldVisible('useType') && (
                            <Col span={8}>
                                <FormItem
                                    name="useType"
                                    label="Use type"
                                    rules={[{ required: true }]}
                                    tooltip={
                                        <>
                                            This helps verify the payee's entitlement to input tax credits. The ITCE %
                                            should align with the extent to which the item is used for business
                                            purposes.
                                        </>
                                    }
                                >
                                    <Select<CompensationPaymentUseType>
                                        options={Object.entries(COMPENSATION_PAYMENT_USE_TYPES_LABELS).map(
                                            ([key, value]) => ({
                                                label: value,
                                                value: key,
                                            }),
                                        )}
                                        onChange={(value) => {
                                            form.setFieldsValue({
                                                inputTaxCreditEntitlement:
                                                    value === 'PERSONAL'
                                                        ? new Decimal(0)
                                                        : compensation?.inputTaxCreditEntitlement || new Decimal(100),
                                            });
                                        }}
                                    />
                                </FormItem>
                            </Col>
                        )}
                        {isFieldVisible('inputTaxCreditEntitlement') && (
                            <Col span={8}>
                                <FormItem
                                    name="inputTaxCreditEntitlement"
                                    label={`${compensation?.payeeType === 'POLICY_HOLDER' ? 'Policy holder' : 'Third party'} ITCE %`}
                                    rules={buildPercentageInputRules({ isRequired: true })}
                                    tooltip={
                                        <>
                                            This shows the ITCE % of the{' '}
                                            {compensation?.payeeType === 'POLICY_HOLDER'
                                                ? 'policy holder'
                                                : 'third party'}
                                            . If the item is for business or mixed use, you can override the ITCE here
                                            according to the level of business use.
                                        </>
                                    }
                                >
                                    <PercentageInput className="w-full" isDisabled={useType === 'PERSONAL'} />
                                </FormItem>
                            </Col>
                        )}
                    </Row>

                    <Row gutter={16}>
                        {compensation.isRegisteredForGst && (
                            <>
                                <Col span={8}>
                                    <FormItem
                                        label="Net amount"
                                        name="netAmount"
                                        rules={buildCurrencyInputRules({ isRequired: true })}
                                        tooltip="Enter net or total amount. Total amount will be calculated automatically."
                                    >
                                        <CurrencyInput />
                                    </FormItem>
                                </Col>
                                <Col span={8}>
                                    <FormItem
                                        label="GST amount"
                                        name="gstAmount"
                                        rules={buildCurrencyInputRules(
                                            compensation?.payeeType === 'SUPPLIER' ? { gtZero: true } : undefined,
                                        )}
                                    >
                                        <CurrencyInput
                                            isDisabled={
                                                !isGSTApplicable ||
                                                !isThisPurchaseOfMotorVehicle ||
                                                !isThisPurchaseOfRealPropertyUnderMarginScheme
                                            }
                                        />
                                    </FormItem>
                                </Col>
                            </>
                        )}
                        <Col span={8}>
                            <FormItem
                                label={compensation.isRegisteredForGst ? 'Total amount' : 'Amount'}
                                name="totalAmount"
                                rules={buildCurrencyInputRules({ isRequired: true, gtZero: true })}
                                tooltip={
                                    compensation.isRegisteredForGst
                                        ? 'Enter net or total amount. Net amount will be calculated automatically.'
                                        : undefined
                                }
                            >
                                <CurrencyInput />
                            </FormItem>
                        </Col>
                    </Row>
                </Form>
            )}
        </OverlayPage>
    );
}
